/**
 * Consent Management Types
 * GDPR-compliant consent tracking
 */

// ============================================
// Consent Categories
// ============================================

/** Consent categories */
export type ConsentCategory =
  | 'necessary'      // Always allowed, functional cookies
  | 'analytics'      // Analytics and tracking
  | 'marketing'      // Marketing and advertising
  | 'preferences'    // User preferences storage

// ============================================
// Consent State
// ============================================

/** Consent state for each category */
export interface ConsentState {
  /** Necessary cookies (always true) */
  necessary: true
  /** Analytics consent */
  analytics: boolean
  /** Marketing consent */
  marketing: boolean
  /** Preferences consent */
  preferences: boolean
  /** Whether user has made a decision */
  hasDecided: boolean
  /** Whether banner should be shown */
  showBanner: boolean
  /** Whether settings modal is open */
  settingsOpen: boolean
}

/** Initial consent state */
export const initialConsentState: ConsentState = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  hasDecided: false,
  showBanner: true,
  settingsOpen: false,
}

// ============================================
// Consent Metadata
// ============================================

/** How consent was given */
export type ConsentMethod = 'banner' | 'settings' | 'api'

/** Consent metadata */
export interface ConsentMetadata {
  /** When consent was given/updated */
  timestamp: string
  /** Version of privacy policy accepted */
  policyVersion: string
  /** How consent was given */
  method: ConsentMethod
  /** IP country (for legal compliance) */
  region?: string
}

// ============================================
// Consent Record (Stored)
// ============================================

/** Complete consent record for storage */
export interface ConsentRecord {
  /** Consent state by category */
  state: Omit<ConsentState, 'showBanner' | 'settingsOpen'>
  /** Metadata about consent */
  metadata: ConsentMetadata
}

// ============================================
// Consent Actions
// ============================================

/** Update consent payload */
export interface UpdateConsentPayload {
  category: Exclude<ConsentCategory, 'necessary'>
  granted: boolean
}

// ============================================
// Storage Keys
// ============================================

/** Storage keys for consent */
export const CONSENT_STORAGE_KEY = 'spcc-consent'
export const CONSENT_VERSION_KEY = 'spcc-consent-version'
